// Trail-difficulty icon set picker.
//
// A trail's primary_use drives which visual vocabulary its difficulty
// icon uses. Mountain biking has the IMBA shapes (squiggle / circle /
// square / diamond / double-diamond); alpine + nordic skiing have
// the standard ski-resort symbology; everything else falls back to
// a plain colored circle where the only signal is the colour itself.
//
// Map the primary_use slug → icon-set name so callers don't need to
// know the rule. New uses fall back to the generic circle set so an
// unrecognised value never breaks rendering.

export const ICON_SETS = ['mtb', 'alpine', 'nordic', 'circle'];

export const ICON_SET_LABELS = {
    mtb: 'Mountain biking',
    alpine: 'Alpine skiing',
    nordic: 'Nordic skiing',
    circle: 'Generic (colored circle)',
};

export const ICON_SET_DESCRIPTIONS = {
    mtb: 'IMBA-style shapes — used by mountain biking, gravel, fat-biking.',
    alpine: 'Resort-standard ski signs (green circle, blue square, black diamond, double black).',
    nordic: 'Cross-country skiing signs — same shapes outlined for snow contrast.',
    circle: 'Plain colored discs — used by hiking, running, equestrian, snowshoeing, ATVs, snowmobiling, walking, multi-use, and any custom use.',
};

const PRIMARY_USE_TO_SET = {
    mtb: 'mtb',
    gravel: 'mtb',
    fatbike: 'mtb',
    alpine: 'alpine',
    nordic: 'nordic',
};

export function iconSetForUse(primaryUse) {
    if (!primaryUse) return 'circle';
    return PRIMARY_USE_TO_SET[primaryUse] || 'circle';
}

// Order matters for the Settings preview — most-used first.
export const DIFFICULTY_ORDER = ['easy', 'moderate', 'hard', 'expert', 'pro', 'access'];

// Fallback used when neither the org palette nor the legacy flat
// shape supplies a value. Matches Trail::displayColor's "moderate"
// default so trail polylines never render as a stark CSS-default.
const FALLBACK_DIFFICULTY_COLOR = '#4184c5';

/**
 * Pull a difficulty colour from the per-icon-set palette the server
 * ships (`organisation.difficulty_colors`). Tolerates three shapes
 * so consumers don't have to:
 *
 *   • undefined / null     → falls through to FALLBACK_DIFFICULTY_COLOR
 *   • flat legacy palette  → { easy, moderate, ... } — applied to
 *                            every icon set (the migration window
 *                            during which old saved values still
 *                            ride through).
 *   • nested per-set       → { mtb: { easy, ... }, alpine: {...}, ...}
 *                            — pulls the requested set with mtb as
 *                            fallback, then the requested difficulty.
 */
export function resolveDifficultyColor(palette, iconSet, difficulty) {
    if (!difficulty) return FALLBACK_DIFFICULTY_COLOR;
    if (!palette || typeof palette !== 'object') return FALLBACK_DIFFICULTY_COLOR;
    // Detect nested shape: any of the four icon sets present as a
    // key. Prevents misclassifying a flat palette whose keys happen
    // to overlap with valid difficulty slugs.
    const isNested = ICON_SETS.some((set) => palette[set] && typeof palette[set] === 'object');
    if (isNested) {
        const setBucket = palette[iconSet] || palette.mtb || {};
        return setBucket[difficulty] || palette.mtb?.[difficulty] || FALLBACK_DIFFICULTY_COLOR;
    }
    return palette[difficulty] || FALLBACK_DIFFICULTY_COLOR;
}
